import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Form, Message, Modal } from 'semantic-ui-react';
import { toast } from 'react-semantic-toasts';
import { useGenericPoints } from '../../providers/GenericPointProvider';

const formStyle = {
    form: { display: 'grid', gridTemplateColumns: '30% 1fr', alignItems: 'center', rowGap: '0.75rem', columnGap: '1rem' },
    field: { display: 'contents' },
    label: { fontSize: '1rem', fontWeight: 'bold' },
    input: { width: '100%' },
};

const POINT_TYPE_OPTIONS = [
    { key: 'EP', text: 'Energy Points', value: 'EP' },
    { key: 'VP', text: 'Variable Points', value: 'VP' },
    { key: 'PP', text: 'Position Points', value: 'PP' },
    { key: 'BP', text: 'Binary Points', value: 'BP' },
    { key: 'NP', text: 'Numeric Points', value: 'NP' },
    { key: 'GP', text: 'Global Points', value: 'GP' },
];

const DATA_TYPE_OPTIONS = [
    { key: 'boolean', text: 'Boolean', value: 'boolean' },
    { key: 'numeric', text: 'Numeric', value: 'numeric' },
    { key: 'real', text: 'Real', value: 'real' },
    { key: 'string', text: 'String', value: 'string' },
    { key: 'integer', text: 'Integer', value: 'integer' },
];

export const GenericPointModal = ({ open, setOpen, editingPoint, activePointType }) => {
    const isEdit = !!editingPoint;

    const [pointType, setPointType] = useState('EP');
    const [num, setNum] = useState('');
    const [code, setCode] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [haystackTag, setHaystackTag] = useState('');
    const [brick, setBrick] = useState('');
    const [description, setDescription] = useState('');
    const [units, setUnits] = useState('');
    const [dataType, setDataType] = useState('');
    const [minRangeValue, setMinRangeValue] = useState('');
    const [maxRangeValue, setMaxRangeValue] = useState('');
    const [defaultValue, setDefaultValue] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const { createGenericPoint, updateGenericPoint, getGenericPoints } = useGenericPoints();

    useEffect(() => {
        if (!open) return;
        setError('');
        if (isEdit) {
            setPointType(editingPoint.pointType || '');
            setCode(editingPoint.code || '');
            setDisplayName(editingPoint.displayName || '');
            setHaystackTag(editingPoint.haystackTag || '');
            setBrick(editingPoint.brick || '');
            setDescription(editingPoint.description || '');
            setUnits(editingPoint.units || '');
            setDataType(editingPoint.dataType || '');
            setMinRangeValue(editingPoint.minRangeValue ?? '');
            setMaxRangeValue(editingPoint.maxRangeValue ?? '');
            setDefaultValue(editingPoint.defaultValue ?? '');
        } else {
            setPointType(activePointType || 'EP');
            setNum('');
            setCode('');
            setDisplayName('');
            setHaystackTag('');
            setBrick('');
            setDescription('');
            setUnits('');
            setDataType('');
            setMinRangeValue('');
            setMaxRangeValue('');
            setDefaultValue('');
        }
    }, [open]);

    const handleClose = () => setOpen(false);

    const handleSubmit = async () => {
        if (!isEdit && !num) { setError('Num is required.'); return; }
        if (!isEdit && !Number.isInteger(Number(num))) { setError('Num must be a whole number.'); return; }
        if (!isEdit && !code.trim()) { setError('Code is required.'); return; }
        if (!description.trim()) { setError('Description is required.'); return; }

        setSubmitting(true);
        setError('');
        try {
            const t = (v) => v?.trim() || null;
            const resolvedUnits = pointType === 'PP' ? '%' : t(units);
            if (isEdit) {
                await updateGenericPoint({
                    id: editingPoint.id,
                    displayName: t(displayName),
                    haystackTag: t(haystackTag),
                    brick: t(brick),
                    description: description.trim(),
                    units: resolvedUnits,
                    dataType: dataType || null,
                    minRangeValue: minRangeValue !== '' ? parseFloat(minRangeValue) : null,
                    maxRangeValue: maxRangeValue !== '' ? parseFloat(maxRangeValue) : null,
                    defaultValue: defaultValue !== '' ? parseFloat(defaultValue) : null,
                });
            } else {
                await createGenericPoint({
                    pointType,
                    num: parseInt(num, 10),
                    code: code.trim(),
                    displayName: t(displayName),
                    haystackTag: t(haystackTag),
                    brick: t(brick),
                    description: description.trim(),
                    units: resolvedUnits,
                    dataType: dataType || null,
                    minRangeValue: minRangeValue !== '' ? parseFloat(minRangeValue) : null,
                    maxRangeValue: maxRangeValue !== '' ? parseFloat(maxRangeValue) : null,
                    defaultValue: defaultValue !== '' ? parseFloat(defaultValue) : null,
                });
            }
            await getGenericPoints();
            handleClose();
            toast({
                type: 'success',
                title: 'Success',
                description: isEdit ? 'Generic point updated successfully' : 'Generic point created successfully',
                animation: 'fade down',
                time: 4000,
            });
        } catch (err) {
            if (err?.response?.status === 409) {
                setError('A point with this ID already exists.');
            } else {
                setError('An error occurred. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal size="small" closeIcon open={open} onClose={handleClose} onOpen={() => setOpen(true)}>
            <Modal.Header>{isEdit ? 'Edit Generic Point' : 'Add Generic Point'}</Modal.Header>
            <Modal.Content>
                <Form style={formStyle.form}>
                    {isEdit ? (
                        <>
                            <Form.Field style={formStyle.field}>
                                <label style={formStyle.label}>Point Type</label>
                                <span>{POINT_TYPE_OPTIONS.find(o => o.value === editingPoint.pointType)?.text || editingPoint.pointType}</span>
                            </Form.Field>
                            <Form.Field style={formStyle.field}>
                                <label style={formStyle.label}>ID</label>
                                <span>{editingPoint.id}</span>
                            </Form.Field>
                            <Form.Field style={formStyle.field}>
                                <label style={formStyle.label}>Code</label>
                                <span>{editingPoint.code}</span>
                            </Form.Field>
                        </>
                    ) : (
                        <>
                            <Form.Field style={formStyle.field}>
                                <label style={formStyle.label}>Point Type</label>
                                <Dropdown
                                    placeholder="Point Type"
                                    options={POINT_TYPE_OPTIONS}
                                    value={pointType}
                                    onChange={(e, { value }) => setPointType(value)}
                                    selection
                                    style={formStyle.input}
                                />
                            </Form.Field>
                            <Form.Field style={formStyle.field}>
                                <label style={formStyle.label}>Num *</label>
                                <input
                                    type="number"
                                    placeholder="Num"
                                    style={formStyle.input}
                                    value={num}
                                    onChange={e => setNum(e.target.value)}
                                />
                            </Form.Field>
                            <Form.Field style={formStyle.field}>
                                <label style={formStyle.label}>Code *</label>
                                <input
                                    placeholder="Code"
                                    style={formStyle.input}
                                    value={code}
                                    onChange={e => setCode(e.target.value)}
                                />
                            </Form.Field>
                        </>
                    )}
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Description *</label>
                        <input
                            placeholder="Description"
                            style={formStyle.input}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                        />
                    </Form.Field>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Display Name</label>
                        <input
                            placeholder="Display Name"
                            style={formStyle.input}
                            value={displayName}
                            onChange={e => setDisplayName(e.target.value)}
                        />
                    </Form.Field>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Haystack Tag</label>
                        <input
                            placeholder="Haystack Tag"
                            style={formStyle.input}
                            value={haystackTag}
                            onChange={e => setHaystackTag(e.target.value)}
                        />
                    </Form.Field>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Brick</label>
                        <input
                            placeholder="Brick"
                            style={formStyle.input}
                            value={brick}
                            onChange={e => setBrick(e.target.value)}
                        />
                    </Form.Field>
                    {pointType !== 'PP' && pointType !== 'BP' && (
                        <Form.Field style={formStyle.field}>
                            <label style={formStyle.label}>Units</label>
                            <input
                                placeholder="Units"
                                style={formStyle.input}
                                value={units}
                                onChange={e => setUnits(e.target.value)}
                            />
                        </Form.Field>
                    )}
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Data Type</label>
                        <Dropdown
                            placeholder="Data Type"
                            options={DATA_TYPE_OPTIONS}
                            value={dataType}
                            onChange={(e, { value }) => setDataType(value)}
                            selection
                            clearable
                            style={formStyle.input}
                        />
                    </Form.Field>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Min Range Value</label>
                        <input
                            type="number"
                            placeholder="Min Range Value"
                            style={formStyle.input}
                            value={minRangeValue}
                            onChange={e => setMinRangeValue(e.target.value)}
                        />
                    </Form.Field>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Max Range Value</label>
                        <input
                            type="number"
                            placeholder="Max Range Value"
                            style={formStyle.input}
                            value={maxRangeValue}
                            onChange={e => setMaxRangeValue(e.target.value)}
                        />
                    </Form.Field>
                    {pointType !== 'EP' && pointType !== 'VP' && pointType !== 'PP' && pointType !== 'BP' && pointType !== 'GP' && (
                        <Form.Field style={formStyle.field}>
                            <label style={formStyle.label}>Default Value</label>
                            <input
                                placeholder="Default Value"
                                style={formStyle.input}
                                value={defaultValue}
                                onChange={e => setDefaultValue(e.target.value)}
                            />
                        </Form.Field>
                    )}
                    {error && <Message negative content={error} style={{ gridColumn: '1 / -1' }} />}
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button className="ui primary button" onClick={handleSubmit} loading={submitting} disabled={submitting}>
                    {isEdit ? 'Save' : 'Add Point'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};
